import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import { BillingPage } from './billingUi';
import {
  fetchInvoices,
  fetchInvoice,
  generateInvoice,
  transitionInvoice,
  downloadInvoicePdf,
  sendInvoice,
  recordPayment,
  formatMoney,
} from '../../services/billingApi';
import { formatAppDate } from '../../services/appConfigRuntime';
import { fetchTenants, fetchLookups } from '../../services/licensingApi';

/** Spec §8.5 Invoice Screen — Draft → Approved → Issued; Record Payment */
const BillingInvoices = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [lookups, setLookups] = useState({});
  const [tenantId, setTenantId] = useState(searchParams.get('tenantId') || '');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({
    amount: '',
    paymentMethod: 'ACH',
    paymentReference: '',
    paymentDate: new Date().toISOString().slice(0, 10),
    paymentStatus: 'COMPLETED',
  });
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendInvoiceRow, setSendInvoiceRow] = useState(null);
  const [sendConfirmText, setSendConfirmText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [inv, tens, luk] = await Promise.all([
        fetchInvoices({
          tenantId: tenantId || undefined,
          status: status || undefined,
        }),
        fetchTenants(),
        fetchLookups({ grouped: true }),
      ]);
      setRows(inv);
      setTenants(tens);
      setLookups(luk || {});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, status]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    try {
      setDetail(await fetchInvoice(id));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleGenerate = async () => {
    if (!tenantId) {
      toast.error('Select a tenant');
      return;
    }
    setBusy(true);
    try {
      const inv = await generateInvoice(tenantId, {});
      toast.success(`Draft ${inv.invoiceNumber} created`);
      await load();
      setDetail(inv);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAction = async (action) => {
    if (!detail?.invoiceId) return;
    setBusy(true);
    try {
      const inv = await transitionInvoice(detail.invoiceId, action);
      toast.success(`Invoice ${action.toLowerCase()}d`);
      setDetail(inv);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openSendEmail = async (invoiceId) => {
    if (!invoiceId) return;
    setSendConfirmText('');
    setBusy(true);
    try {
      const inv = detail?.invoiceId === invoiceId ? detail : await fetchInvoice(invoiceId);
      setSendInvoiceRow(inv);
      setSendOpen(true);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmSendEmail = async () => {
    if (!sendInvoiceRow?.invoiceId) return;
    if (sendConfirmText.trim().toLowerCase() !== 'send') {
      toast.error('Type send to confirm');
      return;
    }
    setBusy(true);
    try {
      await sendInvoice(sendInvoiceRow.invoiceId);
      toast.success('Invoice email sent');
      setSendOpen(false);
      setSendConfirmText('');
      setSendInvoiceRow(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handlePay = async () => {
    if (!detail?.invoiceId) return;
    setBusy(true);
    try {
      const inv = await recordPayment({
        invoiceId: detail.invoiceId,
        amount: payForm.amount,
        paymentMethod: payForm.paymentMethod,
        paymentReference: payForm.paymentReference,
        paymentDate: payForm.paymentDate,
        paymentStatus: payForm.paymentStatus || 'COMPLETED',
      });
      toast.success('Payment recorded');
      setPayOpen(false);
      setDetail(inv);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const statuses = lookups.INVOICE_STATUS || [];
  const methods = lookups.PAYMENT_METHOD || [
    { code: 'ACH', label: 'ACH' },
    { code: 'CARD', label: 'Card' },
    { code: 'CHECK', label: 'Check' },
  ];

  return (
    <BillingPage>
      <BillingNav title="Invoices" />

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Tenant</InputLabel>
          <Select
            label="Tenant"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {tenants.map((t) => (
              <MenuItem key={t.tenantId || t.tenant_id} value={String(t.tenantId || t.tenant_id)}>
                {t.tenantName || t.tenant_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Invoice status</InputLabel>
          <Select label="Invoice status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {statuses.map((s) => (
              <MenuItem key={s.code} value={s.code}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" disabled={busy || !tenantId} onClick={handleGenerate}>
          Generate Draft
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Invoice #</TableCell>
              <TableCell>Tenant</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Invoice date</TableCell>
              <TableCell>Due date</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Open</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="text.secondary">No invoices.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.invoiceId} hover>
                  <TableCell>{r.invoiceNumber}</TableCell>
                  <TableCell>{r.tenantName || r.legalCompanyName}</TableCell>
                  <TableCell>
                    {r.billingPeriodStart ? String(r.billingPeriodStart).slice(0, 10) : '—'} →{' '}
                    {r.billingPeriodEnd ? String(r.billingPeriodEnd).slice(0, 10) : '—'}
                  </TableCell>
                  <TableCell>{r.invoiceDate ? formatAppDate(r.invoiceDate) : '—'}</TableCell>
                  <TableCell>{r.dueDate ? formatAppDate(r.dueDate) : '—'}</TableCell>
                  <TableCell align="right">{formatMoney(r.totalAmount, r.currency)}</TableCell>
                  <TableCell align="right">{formatMoney(r.balanceDue, r.currency)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.invoiceStatus} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      aria-label="Invoice actions"
                      onClick={(e) => setMenu({ anchor: e.currentTarget, row: r })}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const id = menu?.row?.invoiceId;
            setMenu(null);
            if (id) openDetail(id);
          }}
        >
          View
        </MenuItem>
        {['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'].includes(menu?.row?.invoiceStatus) && (
          <MenuItem
            onClick={async () => {
              const row = menu.row;
              setMenu(null);
              try {
                const inv = await fetchInvoice(row.invoiceId);
                setDetail(inv);
                setPayForm((f) => ({
                  ...f,
                  amount: inv.balanceDue != null ? String(inv.balanceDue) : '',
                }));
                setPayOpen(true);
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Record payment
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            const id = menu?.row?.invoiceId;
            setMenu(null);
            if (id) downloadInvoicePdf(id).catch((e) => toast.error(e.message));
          }}
        >
          PDF
        </MenuItem>
        {menu?.row && !['DRAFT', 'VOID'].includes(menu.row.invoiceStatus) && (
          <MenuItem
            onClick={() => {
              const id = menu.row.invoiceId;
              setMenu(null);
              openSendEmail(id);
            }}
          >
            Send email…
          </MenuItem>
        )}
      </Menu>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Invoice {detail?.invoiceNumber}{' '}
          <Chip size="small" label={detail?.invoiceStatus} sx={{ ml: 1 }} />
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2">Tenant: {detail.tenantName}</Typography>
                  <Typography variant="body2">Company: {detail.legalCompanyName}</Typography>
                  <Typography variant="body2">Currency: {detail.currency}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Period: {String(detail.billingPeriodStart || '').slice(0, 10)} →{' '}
                    {String(detail.billingPeriodEnd || '').slice(0, 10)}
                  </Typography>
                  <Typography variant="body2">
                    Invoice date: {detail.invoiceDate ? formatAppDate(detail.invoiceDate) : '—'}
                  </Typography>
                  <Typography variant="body2">
                    Due date: {detail.dueDate ? formatAppDate(detail.dueDate) : '—'}
                  </Typography>
                </Grid>
              </Grid>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Description</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Discount</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell align="right">Line total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detail.lines || []).map((l) => (
                    <TableRow key={l.invoiceLineId}>
                      <TableCell>{l.description}</TableCell>
                      <TableCell>{l.productCode || '—'}</TableCell>
                      <TableCell align="right">{l.quantity}</TableCell>
                      <TableCell>{l.unit || '—'}</TableCell>
                      <TableCell align="right">
                        {formatMoney(l.unitPrice, detail.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(l.discountAmount, detail.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(l.taxAmount, detail.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(l.totalAmount, detail.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography>Subtotal: {formatMoney(detail.subtotal, detail.currency)}</Typography>
                <Typography>Discount: {formatMoney(detail.discountAmount, detail.currency)}</Typography>
                <Typography>Tax: {formatMoney(detail.taxAmount, detail.currency)}</Typography>
                {detail.taxJurisdiction && (
                  <Typography variant="body2" color="text.secondary">
                    Jurisdiction: {detail.taxJurisdiction}
                  </Typography>
                )}
                <Typography>Credits: {formatMoney(detail.creditAmount, detail.currency)}</Typography>
                <Typography variant="h6">
                  Total: {formatMoney(detail.totalAmount, detail.currency)}
                </Typography>
                <Typography>Paid: {formatMoney(detail.amountPaid, detail.currency)}</Typography>
                <Typography>Balance due: {formatMoney(detail.balanceDue, detail.currency)}</Typography>
              </Box>
              {(detail.history || []).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">History</Typography>
                  {(detail.history || []).map((h, i) => (
                    <Typography key={i} variant="body2" color="text.secondary">
                      {h.action} {h.oldValue || ''} → {h.newValue || ''} {h.performedBy ? `· ${h.performedBy}` : ''}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
          {detail?.invoiceStatus === 'DRAFT' && (
            <Button disabled={busy} variant="contained" onClick={() => handleAction('APPROVE')}>
              Approve
            </Button>
          )}
          {detail?.invoiceStatus === 'APPROVED' && (
            <Button disabled={busy} variant="contained" onClick={() => handleAction('ISSUE')}>
              Issue
            </Button>
          )}
          {['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'].includes(detail?.invoiceStatus) && (
            <Button
              disabled={busy}
              variant="contained"
              onClick={() => {
                setPayForm((f) => ({
                  ...f,
                  amount: detail.balanceDue != null ? String(detail.balanceDue) : '',
                }));
                setPayOpen(true);
              }}
            >
              Record Payment
            </Button>
          )}
          {detail?.invoiceStatus === 'VOID' && (
            <Button disabled={busy} onClick={() => handleAction('REISSUE')}>Reissue</Button>
          )}
          {detail && !['PAID', 'VOID', 'WRITTEN_OFF'].includes(detail.invoiceStatus) && (
            <Button disabled={busy} color="warning" onClick={() => handleAction('VOID')}>
              Void
            </Button>
          )}
          {['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'].includes(detail?.invoiceStatus) && (
            <Button disabled={busy} onClick={() => handleAction('DISPUTE')}>Dispute</Button>
          )}
          {detail && !['DRAFT', 'VOID', 'PAID', 'WRITTEN_OFF'].includes(detail.invoiceStatus) && (
            <Button disabled={busy} onClick={() => handleAction('WRITE_OFF')}>Write off</Button>
          )}
          {detail?.invoiceId && (
            <Button disabled={busy} onClick={() => downloadInvoicePdf(detail.invoiceId).catch((e) => toast.error(e.message))}>
              PDF
            </Button>
          )}
          {detail && !['DRAFT', 'VOID'].includes(detail.invoiceStatus) && (
            <Button disabled={busy} onClick={() => openSendEmail(detail.invoiceId)}>
              Send email…
            </Button>
          )}
          <Button onClick={() => navigate('/admin/billing/payments')}>Payments</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Amount"
                value={payForm.amount}
                onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Method</InputLabel>
                <Select
                  label="Method"
                  value={payForm.paymentMethod}
                  onChange={(e) => setPayForm((f) => ({ ...f, paymentMethod: e.target.value }))}
                >
                  {methods.map((m) => (
                    <MenuItem key={m.code || m} value={m.code || m}>
                      {m.label || m.code || m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Reference or provider token"
                value={payForm.paymentReference}
                onChange={(e) => setPayForm((f) => ({ ...f, paymentReference: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Payment date"
                InputLabelProps={{ shrink: true }}
                value={payForm.paymentDate}
                onChange={(e) => setPayForm((f) => ({ ...f, paymentDate: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={payForm.paymentStatus || 'COMPLETED'}
                  onChange={(e) => setPayForm((f) => ({ ...f, paymentStatus: e.target.value }))}
                >
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="FAILED">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={handlePay}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendOpen} onClose={() => !busy && setSendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Send invoice email</DialogTitle>
        <DialogContent dividers>
          {sendInvoiceRow ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Invoice <strong>{sendInvoiceRow.invoiceNumber}</strong>
                {' · '}
                {formatMoney(sendInvoiceRow.totalAmount, sendInvoiceRow.currency)}
                {' · '}
                {sendInvoiceRow.invoiceStatus}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This emails the billing contact. Check the PDF / amounts first. Type <strong>send</strong> below to
                confirm.
              </Typography>
              <TextField
                fullWidth
                size="small"
                autoFocus
                label='Type "send" to confirm'
                value={sendConfirmText}
                onChange={(e) => setSendConfirmText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmSendEmail();
                }}
              />
            </>
          ) : (
            <Typography variant="body2">Loading invoice…</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setSendOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={busy || sendConfirmText.trim().toLowerCase() !== 'send' || !sendInvoiceRow}
            onClick={confirmSendEmail}
          >
            {busy ? 'Sending…' : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>
    </BillingPage>
  );
};

export default BillingInvoices;
